import * as React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "icons/components";
import Link from "next/link";
import { ELLIPSIS, pagination } from "@/utils/pagination";
import clsx from "clsx";
import { useRouter } from "next/router";

type PaginationProps = {
	currentPage: number;
	pageCount: number;
	/** Will be appended with page number */
	baseHref: string;
	/** Label for navigation landmark, e.g. "Pagination for projects" */
	accessibilityLabel: string;
	scroll?: boolean;
};

export function Pagination({
	currentPage,
	pageCount,
	baseHref,
}: PaginationProps): JSX.Element {
	const { query } = useRouter();
	const canNavigateBack = currentPage > 1;
	const canNavigateForward = pageCount > currentPage;

	// use new URL and search params to navigate to the next page
	const navigateToPage = (page: number) => {
		// @ts-ignore
		const searchParams = new URLSearchParams(query);
		searchParams.set("page", String(page));

		return baseHref + "?" + searchParams.toString();
	};

	return (
		<>
			<div className="flex items-center justify-between w-full px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
				<div className="flex justify-between flex-1 sm:hidden">
					{canNavigateBack ? (
						<Link
							href={navigateToPage(currentPage - 1)}
							className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
						>
							Vorige pagina
						</Link>
					) : null}
					{canNavigateForward ? (
						<Link
							href={navigateToPage(currentPage + 1)}
							className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
						>
							Volgende pagina
						</Link>
					) : null}
				</div>
				<div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
					<div />
					<div>
						<nav
							className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm"
							aria-label="Pagination"
						>
							{canNavigateBack ? (
								<Link
									href={navigateToPage(currentPage - 1)}
									className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 rounded-l-md"
								>
									<span className="sr-only">Vorige pagina</span>
									<ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
								</Link>
							) : null}
							{/* Current: "z-10 bg-indigo-50 border-indigo-500 text-indigo-600", Default: "bg-white border-gray-300 text-gray-500 hover:bg-gray-50" */}
							{pagination(currentPage, pageCount).map((page) => {
								if (page === ELLIPSIS) {
									return (
										<button
											type="button"
											className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
										>
											<span
												role="img"
												aria-label={"..."}
												data-testid="ellipsis"
											>
												&hellip;
											</span>
										</button>
									);
								}

								return (
									<PaginationItem
										key={page}
										page={page}
										active={currentPage === page}
										href={navigateToPage(page)}
									/>
								);
							})}
							{canNavigateForward ? (
								<Link
									href={navigateToPage(currentPage + 1)}
									className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 rounded-r-md"
								>
									<span className="sr-only">Volgende pagina</span>
									<ChevronRightIcon className="w-5 h-5" aria-hidden="true" />
								</Link>
							) : null}
						</nav>
					</div>
				</div>
			</div>
		</>
	);
}

type PaginationItemProps = {
	page: number;
	active: boolean;
	href: string;
	scroll?: boolean;
};

export function PaginationItem({
	page,
	active,
	href,
	scroll,
}: PaginationItemProps): JSX.Element {
	const baseClasses =
		"relative inline-flex items-center px-4 py-2 text-gray-500 text-sm font-medium hover:bg-gray-50 bg-white border border-gray-300";
	const interactiveClasses = "hover:bg-gray-50";

	return (
		<Link
			href={href}
			scroll={scroll}
			className={clsx(
				baseClasses,
				active
					? "text-primaryDark bg-primaryLight border-primary"
					: interactiveClasses,
			)}
			aria-label={`Pagina ${page}`}
			aria-current={active ? "page" : undefined}
			data-testid="pagination-item-link"
		>
			{page}
		</Link>
	);
}
